import { GitBranch, BarChart3, Target, Zap, LineChart, Database, MousePointer, Mail, Share2, DollarSign, TrendingUp, PieChart } from "lucide-react";
import SubServicePageLayout from "@/components/SubServicePageLayout";

const services = [
  { name: "Multi-Touch Attribution", icon: GitBranch, components: ["Touchpoint mapping", "Model selection", "Cross-channel analysis"], outcome: "Understand the full customer journey." },
  { name: "Incrementality Testing", icon: Target, components: ["Geo-lift studies", "Holdout testing", "Causal inference"], outcome: "Prove true marketing impact." },
  { name: "Marketing Mix Modeling", icon: BarChart3, components: ["Historical analysis", "Budget optimization", "Scenario planning"], outcome: "Optimize channel allocation." },
  { name: "Custom Modeling", icon: LineChart, components: ["Data-driven attribution", "Algorithmic models", "Custom solutions"], outcome: "Models tailored to your business." },
];

const processSteps = [
  { number: "01", title: "Discovery", description: "Understand your channels, data, and business questions." },
  { number: "02", title: "Data Assessment", description: "Evaluate data quality and integration needs." },
  { number: "03", title: "Model Design", description: "Select and configure attribution approach." },
  { number: "04", title: "Implementation", description: "Build and validate attribution models." },
  { number: "05", title: "Optimization", description: "Ongoing refinement based on results." },
];

const technologies = [
  { category: "Attribution", items: ["Northbeam", "Triple Whale", "Rockerbox", "AppsFlyer"] },
  { category: "Analysis", items: ["BigQuery", "Python", "R", "Looker"] },
];

const faqs = [
  { question: "Which attribution model should we use?", answer: "It depends on your channels and sales cycle. We help you choose the right approach." },
  { question: "How do you handle iOS privacy changes?", answer: "We combine probabilistic modeling with server-side tracking and incrementality testing." },
];

const qualification = {
  forYou: ["You spend on multiple marketing channels", "You need to optimize budget allocation", "You're tired of conflicting platform data"],
  notForYou: ["You only use one marketing channel", "You're not ready to act on attribution insights"],
};

// Attribution Data Flow
const dataFlowchartNodes = [
  // Sources
  { id: "paid-ads", label: "Paid Ads", description: "Google, Meta, TikTok impression & click data", icon: MousePointer, type: "source" as const },
  { id: "organic", label: "Organic Traffic", description: "SEO, direct, referral touchpoints", icon: Share2, type: "source" as const },
  { id: "email-sms", label: "Email & SMS", description: "Campaign engagement and conversion events", icon: Mail, type: "source" as const },
  
  // Transform
  { id: "data-unification", label: "Data Unification", description: "Combine and deduplicate cross-channel touchpoints", icon: Database, type: "transform" as const },
  { id: "identity-resolution", label: "Identity Resolution", description: "Match anonymous users to known customers", icon: GitBranch, type: "transform" as const },
  
  // Decision
  { id: "attribution-model", label: "Attribution Model", description: "Apply MTA, MMM, or hybrid attribution logic", icon: BarChart3, type: "decision" as const },
  { id: "incrementality-layer", label: "Incrementality Layer", description: "Validate true lift with holdout testing", icon: Target, type: "decision" as const },
  
  // Output
  { id: "channel-efficiency", label: "Channel Efficiency", description: "True ROAS and CAC by channel", icon: TrendingUp, type: "output" as const },
  { id: "budget-allocation", label: "Budget Allocation", description: "Data-driven spend recommendations", icon: DollarSign, type: "output" as const },
  { id: "portfolio-view", label: "Portfolio View", description: "Unified performance dashboard", icon: PieChart, type: "output" as const },
];

const dataFlowchartConnections = [
  { from: "paid-ads", to: "data-unification" },
  { from: "organic", to: "data-unification" },
  { from: "email-sms", to: "data-unification" },
  { from: "data-unification", to: "identity-resolution" },
  { from: "identity-resolution", to: "attribution-model" },
  { from: "attribution-model", to: "incrementality-layer" },
  { from: "incrementality-layer", to: "channel-efficiency" },
  { from: "incrementality-layer", to: "budget-allocation" },
  { from: "channel-efficiency", to: "portfolio-view" },
  { from: "budget-allocation", to: "portfolio-view" },
];

const AttributionModeling = () => (
  <SubServicePageLayout
    seoTitle="Attribution Modeling Services"
    seoDescription="Multi-touch attribution and marketing mix modeling. Understand what drives conversions and optimize your marketing investment."
    canonicalUrl="https://xperfomax.com/analytics/attribution-modeling"
    category="analytics"
    categoryLabel="Analytics / Attribution Modeling"
    headline="Attribution that reveals truth."
    subheadline="We build attribution models that show what's actually driving results, so you can invest with confidence."
    problemTitle="Why attribution is broken."
    problems={["Platform data conflicts with platform data.", "Last-click ignores the full journey.", "No way to prove incrementality."]}
    systemTitle="Attribution solutions that work."
    services={services}
    processTitle="From confusion to clarity."
    processSteps={processSteps}
    dataFlowchart={{
      title: "From touchpoints to budget decisions.",
      label: "Attribution Flow",
      description: "See how raw channel data transforms into actionable budget allocation insights.",
      nodes: dataFlowchartNodes,
      connections: dataFlowchartConnections,
    }}
    technologies={technologies}
    qualification={qualification}
    faqs={faqs}
    finalCtaTitle="Ready for attribution clarity?"
    finalCtaDescription="Let's build attribution you can trust."
  />
);

export default AttributionModeling;
